import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import PageItemCreateModal from './PageItemCreateModal';
import PageItemDeleteModal from './PageItemDeleteModal';
import PageItemQrDownloadModal from './PageItemQrDownloadModal';
import { PagesContext } from '../../contexts/PagesContext';
import Paginator from '../../components/Paginator';
import { displayOptions, sortOptions } from '../../utils/my';
import PageItemDuplicateModal from './PageItemDuplicateModal';
import CreatePageFromTemplateModal from './CreatePageFromTemplateModal';
import { PageTemplateCategoriesContext } from '../../contexts/PageTemplateCategoriesContext';
import { useUserContext } from '../../contexts/UserContext';
import PageItem from './PageItem';
import ModalComponent from '../../components/ModalComponent';
import CheckPageStatuses from './CheckPageStatuses';

const Index = () => {
  const { user } = useUserContext();
  const [pageDuplicateId, setPageDuplicateId] = useState<string | null>(null);
  const [pageDeleteId, setPageDeleteId] = useState<string | null>(null);
  const [titleSettingId, setTitleSettingId] = useState<string | null>(null);
  const [pageQrDownloadId, setPageQrDownloadId] = useState<string | null>(null);
  const [selectedSortValue, setSelectedSortValue] = useState('');
  const [selectedDisplayOption, setSelectedDisplayOption] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [openPageCreationModal, setOpenPageCreationModal] = useState(false);
  const [selectPageTemplateModalOpen, setSelectPageTemplateModalOpen] = useState(false);
  const [canPublishPage, setCanPublishPage] = useState(false);

  const navigate = useNavigate();
  const {
    pageIds,
    pageDatas,
    List,
    Delete,
    Get,
    paginationPageInfo,
    total,
    totalPublishedPages,
    links,
    getPageList,
    Duplicate,
    GetQrCode,
    contextErrors,
    clearErrors,
  } = useContext(PagesContext);

  const pagesContext = useContext(PagesContext);

  const deletePageTitle = useMemo(() => pageDatas.find((p) => p.id === pageDeleteId)?.page_setting.title, [pageDeleteId, pageDatas]);

  const duplicatePageTitle = useMemo(() => pageDatas.find((p) => p.id === pageDuplicateId)?.page_setting.title, [pageDuplicateId, pageDatas]);

  const { requestPageCategoryList } = useContext(PageTemplateCategoriesContext);

  const {
    UpdateSetting, Create,
  } = pagesContext;

  useEffect(() => {
    requestPageCategoryList();
  }, [requestPageCategoryList]);

  useEffect(() => {
    setCanPublishPage(false);
    // iterate through the pages and check if there is a published page
    const hasPublishedPages = pageDatas.some((page) => page.page_setting?.is_published);

    // if user has no published pages, he can publish pages equal to the number of pages his plan allows
    if (!hasPublishedPages) {
      setCanPublishPage(true);
      return;
    }

    // if user has published pages, he cannot publish more pages than his plan allows
    if (total < Number(user.myPlan?.feat_pages)) {
      setCanPublishPage(false);
    }
  }, [canPublishPage, pageDatas, total, user.has_pendences, user.myPlan?.feat_pages]);

  // 初回全部ローディングします
  useEffect(() => {
    List(paginationPageInfo.current_page, selectedSortValue, selectedDisplayOption);
  }, [List, paginationPageInfo.current_page, selectedDisplayOption, selectedSortValue]);

  // pageIdsが変換するたびに、新しいデータを更新します
  useEffect(() => {
    for (let i = 0; i < pageIds.length; i++) {
      const pageId = pageIds[i];
      // また取得していない場合
      if (!pageDatas.some((data) => data.id === pageId)) {
        Get(pageId);
      }
    }
  }, [pageIds, pageDatas, Get]);

  const handleDelete = (id: string, sort: string, display: string) => {
    Delete(id, sort, display);
  };

  const changeNewPageTitle = (id: string, title: string) => {
    UpdateSetting(id, { title }, () => { Get(id); });
  };

  const handleCreatePage = (title: string) => {
    const params = { template_id: '' };
    if (selectedTemplateId != null) {
      params.template_id = selectedTemplateId;
    }
    Create(JSON.stringify(params), (id: string) => {
      navigate(`/pages/${id}`);
      changeNewPageTitle(id, title);
    });
    setTitleSettingId(null);
  };

  const handleTitleCreateAndUpdate = (title: string) => {
    if (title === '' || title.trim().length === 0) {
      return;
    }
    if (titleSettingId === '') {
      handleCreatePage(title);
      return;
    }

    UpdateSetting(titleSettingId, {
      title,
    }, () => {
      setTitleSettingId(null);
      // Refresh the page data
      getPageList({
        page: paginationPageInfo.current_page,
        sort: selectedSortValue,
        display: selectedDisplayOption,
      });
    });
  };

  const downloadQrCode = (result: string) => {
    const imgBase64 = String(result);
    const base64String = imgBase64.split(',')[1]; // Remove the data URI prefix

    const charCodes = Array.from(atob(base64String), (char) => char.charCodeAt(0));
    const blob = new Blob([new Uint8Array(charCodes)], { type: 'image/png' });
    const imageUrl = URL.createObjectURL(blob);

    // Create a new canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Set the canvas dimensions
    canvas.width = 1080; // Set your desired width
    canvas.height = 2340; // Set your desired height

    // Draw a white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load the image
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      // Calculate the position to center the image
      const x = (canvas.width - 800) / 2;
      const y = (canvas.height - 800) / 2;

      // Draw the image at the center
      ctx.drawImage(img, x, y, 800, 800);

      // Create a link to download the canvas as an image
      const a = document.createElement('a');
      // Convert the canvas to a data URL (JPEG format)
      a.href = canvas.toDataURL('image/jpeg');
      a.download = `qrcode_${pageQrDownloadId}.jpeg`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };
  };

  const handleQrDownload = (url: string) => {
    if (url === '' || url.trim().length === 0) {
      return;
    }

    GetQrCode(pageQrDownloadId, {
      url,
    }, (result, hasError) => {
      if (hasError) {
        return;
      }

      downloadQrCode(result);

      setPageQrDownloadId(null);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      makeRequest();
    });
  };

  const selectedPageInfo = useMemo(() => {
    const targetIds = [titleSettingId, pageQrDownloadId];
    return pageDatas.find((p) => targetIds.includes(p.id));
  }, [pageDatas, titleSettingId, pageQrDownloadId]);

  const changePage = (page: number) => {
    getPageList({
      page,
      sort: selectedSortValue,
      display: selectedDisplayOption,
    });
  };

  const makeRequest = () => {
    getPageList({
      page: 1,
      sort: selectedSortValue,
      display: selectedDisplayOption,
    });
  };

  const makeSortRequest = (sort: string) => {
    getPageList({
      page: 1,
      sort,
      display: selectedDisplayOption,
    });
  };

  const makeDisplayRequest = (display: string) => {
    getPageList({
      page: 1,
      sort: selectedSortValue,
      display,
    });
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setSelectedSortValue(value);
    makeSortRequest(value);
  };

  const handleDisplayOptions = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setSelectedDisplayOption(value);
    makeDisplayRequest(value);
  };

  const [loadingSave, setLoadingSave] = useState(false);

  const duplicatePage = () => {
    setLoadingSave(true);
    Duplicate(pageDuplicateId, () => {
      getPageList({
        page: paginationPageInfo.current_page,
        sort: selectedSortValue,
        display: selectedDisplayOption,
      });
      setPageDuplicateId(null);
      setLoadingSave(false);
    });
  };

  return (
    <div className="main">
      <main
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 65px)',
        }}
      >
        <CheckPageStatuses noAutoRefresh />

        <Box
          display="flex"
          flexDirection="column"
          className="container-fluid p-3"
        >
          <div className="mb-3">
            <h3 className="h3 d-inline align-middle">My page</h3>
            <div
              className="float-end mr-1 d-inline-flex align-items-center gap-2"
              style={{
                border: '2px solid #2664b1',
                borderRadius: '8px',
                padding: '6px 10px',
              }}
            >
              <span className="fw-bold">+Page:</span>
              <button
                type="button"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#pageTitleModal"
                onClick={() => setTitleSettingId('')}
              >
                Blank
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setSelectPageTemplateModalOpen(true)}
              >
                Templates
              </button>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-12">
              <div className="d-flex align-items-center gap-2">
                <div>
                  Pages published:
                  {' '}
                  {totalPublishedPages}
                  {' '}
                  of
                  {' '}
                  {total}
                  {' '}
                  pages
                </div>
                <div className="flex-fill" />
                <div className="d-flex align-items-center gap-2">
                  <div className="text-nowrap">Sort:</div>
                  <select className="form-select" value={selectedSortValue} onChange={handleSelectChange}>
                    {sortOptions.map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="text-nowrap">Filter:</div>
                  <select className="form-select" value={selectedDisplayOption} onChange={handleDisplayOptions}>
                    {displayOptions.map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <Box flex={1} className="row mt-1">
            <div className="d-flex mt-2">
              <div className="page-item-count">
                <div style={{ fontSize: 14 }}>
                  {`${paginationPageInfo.from ?? 0}-${paginationPageInfo.to ?? 0} of ${total}`}
                </div>
              </div>
              <Paginator currentPage={paginationPageInfo.current_page} changePage={changePage} links={links} />

            </div>
          </Box>

          <Box
            flex={8}
            className="row mt-3"
          >
            <Box className="col-12">
              {pageDatas.map((data) => (
                <PageItem
                  key={data.id}
                  page={data}
                  handleEdit={() => setTitleSettingId(data.id)}
                  handleDelete={() => setPageDeleteId(data.id)}
                  handleDuplicate={() => setPageDuplicateId(data.id)}
                  handleQrDownload={() => {
                    setPageQrDownloadId(data.id);
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box flex={1} className="row mt-1 block-list-footer">
            <div className="d-flex mt-2">
              <div className="page-item-count">
                <div style={{ fontSize: 14 }}>
                  {`${paginationPageInfo.from ?? 0}-${paginationPageInfo.to ?? 0} of ${total}`}
                </div>
              </div>
              <Paginator currentPage={paginationPageInfo.current_page} changePage={changePage} links={links} />

            </div>
          </Box>

        </Box>
        <PageItemCreateModal
          open={titleSettingId != null || openPageCreationModal}
          handleClose={() => {
            setTitleSettingId(null);
            setOpenPageCreationModal(false);
          }}
          handleSave={(title) => handleTitleCreateAndUpdate(title)}
          defaultPageTitle={selectedPageInfo?.page_setting?.title || ''}
        />

        {selectPageTemplateModalOpen && (
          <CreatePageFromTemplateModal
            title="Create new page (template)"
            open={selectPageTemplateModalOpen}
            handleTemplateSelected={(templateID) => {
              setSelectedTemplateId(templateID);
              setOpenPageCreationModal(true);
              setTitleSettingId('');
            }}
            handleClose={() => {
              setSelectedTemplateId(null);
              setSelectPageTemplateModalOpen(false);
            }}
          />
        )}

        <PageItemDuplicateModal
          open={pageDuplicateId != null}
          pageTitle={duplicatePageTitle}
          handleClose={() => setPageDuplicateId(null)}
          handleSave={duplicatePage}
          loadingSave={loadingSave}
        />

        <PageItemDeleteModal
          open={pageDeleteId != null}
          pageTitle={deletePageTitle}
          isPublished={pageDatas.find((p) => p.id === pageDeleteId)?.page_setting.is_published}
          handleClose={() => setPageDeleteId(null)}
          handleSave={() => {
            handleDelete(pageDeleteId, selectedSortValue, selectedDisplayOption);
            setPageDeleteId(null);
            getPageList({
              page: paginationPageInfo.current_page,
              sort: selectedSortValue,
              display: selectedDisplayOption,
            });
          }}
        />

        <PageItemQrDownloadModal
          open={pageQrDownloadId != null}
          handleClose={() => setPageQrDownloadId(null)}
          handleSave={(url) => handleQrDownload(url)}
          defaultUrl={selectedPageInfo?.page_setting?.url}
        />

        {contextErrors?.message && (
          <ModalComponent
            show
            closeModal={() => clearErrors()}
            title="Error"
            centered
            hideConfirmButton
            footerClass="d-flex justify-content-center"
            cancelButtonColor="outline-secondary"
            cancelButtonText="Close"
            bodyClassName="text-center"
            bodyStyle={{
              fontSize: '1.25rem',
              padding: '1.25rem',
            }}
          >
            {contextErrors?.message}
          </ModalComponent>
        )}
      </main>

      <footer className="footer d-none">
        <div className="container-fluid">
          <div className="row text-muted">
            <div className="col-6 text-start">
              &copy; 2023 buildself
            </div>
            <div className="col-6 text-end">
              <div className="d-none d-sm-block">ver 1.0.0</div>
            </div>
          </div>
        </div>
      </footer>
      <a className="scroll-to-top rounded"><i className="fa-solid fa-angle-up" /></a>
    </div>
  );
};

export default Index;
